import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { addGst, fetchGstById, updateGst } from "slices/Masters/gst/thunk";
import * as Yup from "yup";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const GSTForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [initialData, setInitialData] = useState({
    GSTName: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchGstById(id))
        .unwrap()
        .then((data: any) =>
          setInitialData({
            GSTName: data.gst_percentage,
          }),
        )
        .catch((err: any) => {
          toast.error(err?.message || "Failed to load GST data");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      GSTName: initialData.GSTName || "",
    },
    validationSchema: Yup.object({
      GSTName: Yup.string().trim().required("Please enter GST %"),
      // .matches(/^\d*\.?\d+$/, "Only numeric values are allowed"),
    }),
    onSubmit: async (values) => {
      const payload = {
        gst_percentage: new Number(values.GSTName),
      };

      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateGst({ id, ...payload })).unwrap();
          toast.success("GST updated successfully");
        } else {
          await dispatch(addGst(payload)).unwrap();
          clearTableState(TableKeys.MASTER.GST);
          toast.success("GST added successfully");
        }
        navigate("/master/gst");
      } catch (error: any) {
        toast.error(
          error?.message || "Something went wrong. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit GST" : "Add GST"}
        titleLink="/master/gst"
        breadcrumbs={[
          { label: "GST", to: "/master/gst" },
          { label: id ? "Edit GST" : "Add GST", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isEdit && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                GST % <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="GSTName"
                placeholder="Enter GST %"
                value={formik.values.GSTName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1");
                  formik.setFieldValue("GSTName", value);
                }}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.GSTName && formik.touched.GSTName}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.GSTName}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton redirectTo="/master/gst" disabled={isSubmitting} />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default GSTForm;
